import React from 'react';

/**
 * Cockpit Table — mono uppercase headers, hairline row dividers,
 * rows wash on hover.
 */
export interface TableColumn<Row> {
  key: string;
  label: React.ReactNode;
  align?: 'left' | 'right' | 'center';
  mono?: boolean;
  /**
   * Column width, ideally a percentage. Declaring one on ANY column switches
   * the table to `table-layout: fixed`, so widths come from this rather than
   * from content — which is what makes columns track the viewport instead of
   * whatever the longest cell happens to be.
   *
   * Tables that declare nothing keep auto layout and are unaffected.
   */
  width?: string;
  /** Let this column's cells wrap. Cells are nowrap by default. */
  wrap?: boolean;
  /**
   * Custom cell. Receives the row's value at `key` and the whole row; defaults to the raw
   * value. Method syntax on purpose: callers type `value` as narrowly as they like.
   */
  render?(value: unknown, row: Row): React.ReactNode;
}

export interface TableProps<Row extends object> {
  columns: TableColumn<Row>[];
  rows: Row[];
  onRowClick?: (row: Row, index: number) => void;
  style?: React.CSSProperties;
  /** Tighter padding and one type step smaller — for wide data tables with many rows. */
  dense?: boolean;
  /** Per-row style on top of the defaults (a status tint, say). */
  rowStyle?: (row: Row) => React.CSSProperties | undefined;
  /** Shown as a single full-width row when `rows` is empty. Omit to render just the header. */
  emptyMessage?: React.ReactNode;
}

export default function Table<Row extends object>({
  columns: columnsProp,
  rows: rowsProp,
  onRowClick,
  style,
  dense = false,
  rowStyle,
  emptyMessage,
}: TableProps<Row>) {
  // A caller passing a misnamed or not-yet-loaded list must not take the whole page down.
  const columns = Array.isArray(columnsProp) ? columnsProp : [];
  const rows = Array.isArray(rowsProp) ? rowsProp : [];
  const cellPad = dense ? '5px 10px' : '12px 16px';
  return (
    <div
      className="ck-table"
      style={{
        background: 'var(--surface-card)',
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-rest)',
        overflow: 'hidden',
        ...style,
      }}
    >
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          // Only opt in when a column asks for it, so every existing table
          // keeps the auto layout it was built against.
          tableLayout: columns.some((c) => c.width) ? 'fixed' : 'auto',
        }}
      >
        {columns.some((c) => c.width) ? (
          <colgroup>
            {columns.map((col) => (
              <col key={col.key} style={col.width ? { width: col.width } : undefined} />
            ))}
          </colgroup>
        ) : null}
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className="t-eyebrow"
                style={{
                  textAlign: col.align || 'left',
                  padding: cellPad,
                  borderBottom: '1px solid var(--border-hairline)',
                  background: 'var(--surface-raised)',
                  whiteSpace: 'nowrap',
                }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && emptyMessage != null ? (
            <tr>
              <td
                colSpan={Math.max(columns.length, 1)}
                style={{ padding: cellPad, textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}
              >
                {emptyMessage}
              </td>
            </tr>
          ) : null}
          {rows.map((row, i) => (
            <tr
              key={(row as { id?: string | number }).id ?? i}
              className="hover-wash"
              onClick={onRowClick ? () => onRowClick(row, i) : undefined}
              style={{
                cursor: onRowClick ? 'pointer' : 'default',
                borderBottom: i < rows.length - 1 ? '1px solid var(--border-hairline)' : 'none',
                ...rowStyle?.(row),
              }}
            >
              {columns.map((col) => {
                const cell = (row as Record<string, unknown>)[col.key];
                return (
                <td
                  key={col.key}
                  className={col.mono ? 't-num' : ''}
                  style={{
                    textAlign: col.align || 'left',
                    padding: cellPad,
                    fontSize: dense ? (col.mono ? 'var(--text-xs)' : 'var(--text-sm)') : col.mono ? 'var(--text-sm)' : 'var(--text-base)',
                    color: 'var(--text-body)',
                    whiteSpace: col.wrap ? 'normal' : 'nowrap',
                    overflowWrap: col.wrap ? 'anywhere' : undefined,
                    verticalAlign: col.wrap ? 'top' : undefined,
                  }}
                >
                  {typeof col.render === 'function' ? col.render(cell, row) : (cell as React.ReactNode)}
                </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
